import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Department, ThemeColors } from '../../types';

interface DepartmentNavProps {
  departments: Department[];
  selectedDepartmentId: number | null;
  onDepartmentClick: (departmentId: number | null) => void;
  themeColors?: ThemeColors;
}

export function DepartmentNav({
  departments,
  selectedDepartmentId,
  onDepartmentClick,
  themeColors
}: DepartmentNavProps) {
  const departmentScrollRef = useRef<HTMLDivElement>(null);
  const megaMenuRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [hoveredDepartmentId, setHoveredDepartmentId] = useState<number | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();

  const isActiveDepartment = (departmentId: number | null) => {
    if (departmentId === null) {
      return selectedDepartmentId === null;
    }
    return Number(selectedDepartmentId) === Number(departmentId);
  };

  // Check if scroll arrows should be visible
  const checkScrollArrows = () => {
    const container = departmentScrollRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    setShowLeftArrow(scrollLeft > 0);
    setShowRightArrow(scrollLeft + clientWidth < scrollWidth - 1);
  };

  // Scroll departments left or right
  const scrollDepartments = (direction: 'left' | 'right') => {
    const container = departmentScrollRef.current;
    if (!container) return;

    const scrollAmount = 200;
    const newScrollLeft = direction === 'left'
      ? container.scrollLeft - scrollAmount
      : container.scrollLeft + scrollAmount;

    container.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    });
  };

  // Update arrow visibility when departments or scroll position changes
  useEffect(() => {
    checkScrollArrows();
    const container = departmentScrollRef.current;
    if (!container) return;

    const handleScroll = () => checkScrollArrows();
    const handleResize = () => checkScrollArrows();

    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);

    return () => {
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [departments]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleCategoryClick = (departmentId: number, categoryId: number) => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('departmentId', departmentId.toString());
    searchParams.set('categoryId', categoryId.toString());
    searchParams.delete('page');
    navigate(`/?${searchParams.toString()}`);
    setHoveredDepartmentId(null);
  };

  const handleDepartmentMouseEnter = (departmentId: number | null, event: React.MouseEvent<HTMLButtonElement>) => {
    // Clear any pending close timeout
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }

    setHoveredDepartmentId(departmentId);

    // Calculate the position of the button relative to the viewport
    // The dropdown is positioned relative to the outer container, not the scrollable list
    const button = event.currentTarget;
    const buttonRect = button.getBoundingClientRect();

    // Find the outer positioned parent (the one with relative class)
    let parent = button.parentElement;
    while (parent && !parent.classList.contains('border-b')) {
      parent = parent.parentElement;
    }

    if (parent) {
      const parentRect = parent.getBoundingClientRect();
      const relativeLeft = buttonRect.left - parentRect.left;
      setDropdownPosition(relativeLeft);
    }
  };

  const handleDepartmentMouseLeave = () => {
    // Set a timeout to close the dropdown
    // This gives the user time to move to the dropdown itself
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredDepartmentId(null);
    }, 150);
  };

  const handleMegaMenuMouseEnter = () => {
    // Clear the close timeout when entering the dropdown
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  };

  const handleMegaMenuMouseLeave = () => {
    setHoveredDepartmentId(null);
  };

  const hoveredDepartment = hoveredDepartmentId
    ? departments.find(d => d.id === hoveredDepartmentId)
    : null;

  return (
    <div
      className="hidden md:block border-b border-gray-600 relative"
      style={{ backgroundColor: themeColors?.left_side_background || '#ffffff' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative flex items-center h-11">
          {/* Left Arrow */}
          {showLeftArrow && (
            <button
              onClick={() => scrollDepartments('left')}
              className="absolute left-0 z-10 p-2 bg-gray-800/90 hover:bg-gray-800 text-white rounded-md shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Scroll departments left"
            >
              <ChevronLeft className="w-4 h-4" aria-hidden="true" />
            </button>
          )}

          {/* Department List */}
          <div
            ref={departmentScrollRef}
            className="flex items-center gap-8 overflow-x-auto scrollbar-hide w-full px-6"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <button
              onClick={() => onDepartmentClick(null)}
              className={`flex-shrink-0 text-xs uppercase tracking-wider font-medium transition-all duration-150 py-2.5 border-b-2 focus:outline-none ${
                isActiveDepartment(null)
                  ? 'border-white'
                  : 'border-transparent'
              }`}
              style={{
                color: isActiveDepartment(null)
                  ? themeColors?.left_side_active_color || '#ffffff'
                  : themeColors?.left_side_color || '#e5e7eb'
              }}
              aria-current={isActiveDepartment(null) ? 'page' : undefined}
            >
              All Departments
            </button>
            {departments.map(dept => (
              <button
                key={dept.id}
                onClick={() => onDepartmentClick(dept.id)}
                onMouseEnter={(e) => handleDepartmentMouseEnter(dept.id, e)}
                onMouseLeave={handleDepartmentMouseLeave}
                className={`flex-shrink-0 flex items-center gap-1 text-xs uppercase tracking-wider font-medium transition-all duration-150 whitespace-nowrap py-2.5 border-b-2 focus:outline-none ${
                  isActiveDepartment(dept.id)
                    ? 'border-white'
                    : 'border-transparent'
                }`}
                style={{
                  color: isActiveDepartment(dept.id)
                    ? themeColors?.left_side_active_color || '#ffffff'
                    : themeColors?.left_side_color || '#e5e7eb'
                }}
                aria-current={isActiveDepartment(dept.id) ? 'page' : undefined}
                aria-expanded={hoveredDepartmentId === dept.id}
              >
                <span>{dept.name} {dept.memo && `(${dept.memo})`}</span>
                <ChevronDown className="w-3 h-3" aria-hidden="true" />
              </button>
            ))}
          </div>

          {/* Right Arrow */}
          {showRightArrow && (
            <button
              onClick={() => scrollDepartments('right')}
              className="absolute right-0 z-10 p-2 bg-gray-800/90 hover:bg-gray-800 text-white rounded-md shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-blue-500"
              aria-label="Scroll departments right"
            >
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {/* Category Dropdown */}
      {hoveredDepartment && hoveredDepartment.productCategories && hoveredDepartment.productCategories.length > 0 && (
        <div
          ref={megaMenuRef}
          onMouseEnter={handleMegaMenuMouseEnter}
          onMouseLeave={handleMegaMenuMouseLeave}
          className="absolute top-full z-50 shadow-lg rounded-b-md border border-gray-600 border-t-0 min-w-[250px] max-w-[400px]"
          style={{
            backgroundColor: themeColors?.left_side_background || '#374151',
            left: `${dropdownPosition}px`
          }}
        >
          <div className="py-2">
            {hoveredDepartment.productCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategoryClick(hoveredDepartment.id, category.id)}
                className="w-full text-left px-4 py-2 text-sm transition-colors duration-150 hover:bg-white/10 focus:outline-none focus:bg-white/10"
                style={{
                  color: themeColors?.left_side_color || '#e5e7eb',
                }}
              >
                <div className="font-medium">{category.name}</div>
                {category.memo && (
                  <div className="text-xs opacity-75 mt-0.5">{category.memo}</div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
